import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IStoreData } from '../@types/Store';
import { AssetsResponse } from '../models/liveshop';
import { Session } from './liveshop.service';
type DiscoverFilters = {
  categoryIds?: number[];
  influencerHandles?: string[];
};
interface UserMediaResponse {
  assets: any[];
  totalAssets: number;
  page: number;
  limit: number;
  nextPage?: number;
}

@Injectable()
export class LiveShopWidgetService {
  constructor(private http: HttpClient) {}

  private getBaseUrl(): string {
    return `${environment.apiBaseUrl}/api`;
  }

  private getStoreId(): string {
    const selected = localStorage.getItem('ss_SelectedStoreId');
    if (selected) {
      return selected;
    }

    const storeRaw = localStorage.getItem('store');
    if (storeRaw) {
      try {
        const parsed = JSON.parse(storeRaw);
        if (parsed?.SelectedStoreId) {
          return String(parsed.SelectedStoreId);
        }
      } catch {
        // ignore parse error
      }
    }

    return '163';
  }

  private buildHeaders(extra: Record<string, string> = {}): HttpHeaders {
    const token = localStorage.getItem('access_token') || '';
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // Sem isso, o backend cai no header `Referer` padrão do navegador, que
      // em requisições cross-origin (app.slicksell.com.br → api.slicksell.
      // com.br) normalmente só traz a origem, sem o path — o bypass de
      // `/edit_widgets/` em checkWidgetPublicAuthorization() nunca bate, e o
      // widget cai na checagem de contain_rule/equal_rule/url_rules de
      // verdade (que falha, já que a URL do admin nunca bate com regra de
      // página real da loja) → 403 no preview do Studio/edit_widgets sempre
      // que o widget tiver regra de URL configurada.
      'X-Full-Referer': window.location.href,
      ...extra
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return new HttpHeaders(headers);
  }

  getAssetsPublic(limit: number | null, type: string, page: number, seed: number, slickId?: string | null): Observable<AssetsResponse> {
    const storeId = this.getStoreId();
    const headers = this.buildHeaders();

    let appendLink = '';
    if (slickId) {
      const cleanSlickId = slickId.split('?')[0];
      appendLink = `&slick_id=${cleanSlickId}`;
      if (cleanSlickId === '1') {
        appendLink = `&l=1&slick_id=${cleanSlickId}`;
      }
    }
    const limitAssets = limit != null ? `&limit=${limit}` : '';
    const url = `${this.getBaseUrl()}/public/admin/store/${storeId}/videoshopping/asset_public?type=${type}${limitAssets}&page=${page}${appendLink}&random=1&seed=${seed}`;

    return this.http.get<AssetsResponse>(url, { headers }).pipe(
      catchError(error => throwError(() => error))
    );
  }

  listarAssetsWidgetPublic(
    widget_id: number | undefined,
    page: number = 1,
    limit: number | null = 10,
    store_id?: number | undefined,
    filters?: DiscoverFilters
  ): Observable<IStoreData> {
    const headers = this.buildHeaders({
      'X-Full-Referer': window.location.href
    });
  
    const resolvedLimit = limit ?? 9;
    const storeId = Number(store_id ?? this.getStoreId());
  
    // ✅ monta querystring com URLSearchParams
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(resolvedLimit));
  
    const categoryIds = (filters?.categoryIds || []).filter(n => Number.isFinite(n));
    if (categoryIds.length) {
      params.set('category_ids', categoryIds.join(','));
    }
  
    const influencers = (filters?.influencerHandles || [])
      .map(s => String(s || '').trim())
      .filter(Boolean);
  
    if (influencers.length) {
      params.set('influencers', influencers.join(','));
    }
  
    const url = `${this.getBaseUrl()}/public/admin/store/${storeId}/videoshopping/widget/${widget_id}/assets_public?${params.toString()}`;
  
    return this.http.get<IStoreData>(url, { headers }).pipe(
      catchError(error => throwError(() => error))
    );
  }

  list_pixel(): Observable<any> {
    const headers = this.buildHeaders();
    const storeId = this.getStoreId();
    console.log('[pixel_public] liveshop-widget.service — storeId:', storeId, '| ss_SelectedStoreId:', localStorage.getItem('ss_SelectedStoreId'), '| SelectedStoreId:', localStorage.getItem('SelectedStoreId'));
    const url = `${this.getBaseUrl()}/public/admin/store/${storeId}/pixel_public`;
    return this.http.get<any>(url, { headers }).pipe(
      catchError(error => throwError(() => error))
    );
  }

  gerarSessaoVisitante(data: any): Observable<any> {
    const headers = this.buildHeaders();
    const url = `${this.getBaseUrl()}/public/store/${this.getStoreId()}/guest_auth`;
    return this.http.post<any>(url, JSON.stringify(data), { headers }).pipe(
      catchError(error => throwError(() => error))
    );
  }

  createDeviceData(data: any): Observable<Session> {
    const headers = this.buildHeaders();
    const url = `${this.getBaseUrl()}/public/store/${this.getStoreId()}/session`;
    return this.http.post<Session>(url, data, { headers }).pipe(
      catchError(error => throwError(() => error))
    );
  }

  createPlayerEvent(
    sessionId: string,
    playerSessionId: string,
    assetUid: string,
    payload: { event: string; details?: any; add_to_cart?: any }
  ): Observable<any> {
    const storeId = this.getStoreId();
    if (!storeId) {
      return throwError(() => new Error('Store ID not found'));
    }

    const headers = this.buildHeaders({
      'X-Full-Referer': window.location.href
    });

    const url = `${this.getBaseUrl()}/public/store/${storeId}/videoshopping/events/session/${sessionId}/player_session/${playerSessionId}/asset/${assetUid}`;

    return this.http.post(url, payload, { headers }).pipe(
      catchError(error => throwError(() => error))
    );
  }

  getUserMediaByHandlePublic(handle: string, source_id: string, page: number = 1, limit: number = 20): Observable<UserMediaResponse> {
    const headers = this.buildHeaders();
    const params = new HttpParams()
      .set('handle', handle)
      .set('source_id', source_id)
      .set('page', String(page))
      .set('limit', String(limit));
    const url = `${this.getBaseUrl()}/admin/store/${this.getStoreId()}/videoshopping/asset`;

    return this.http.get<UserMediaResponse>(url, { headers, params }).pipe(
      catchError(err => throwError(() => err))
    );
  }

  getUserMediaByHandlePublicStore(handle: string, source_id: string, page: number = 1, limit: number = 20): Observable<UserMediaResponse> {
    const headers = this.buildHeaders();
    const params = new HttpParams()
      .set('handle', handle)
      .set('source_id', source_id)
      .set('page', String(page))
      .set('limit', String(limit));
    const url = `${this.getBaseUrl()}/public/admin/store/${this.getStoreId()}/videoshopping/asset_public`;

    return this.http.get<UserMediaResponse>(url, { headers, params }).pipe(
      catchError(err => throwError(() => err))
    );
  }
}
